import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MessageSquare, CheckCheck, ExternalLink, Inbox } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { useNotificationStore } from '../stores/notificationStore';
import Header from '../components/Header';
import MainTabsNav from '../components/MainTabsNav';
import Footer from '../components/Footer';
import AdminSidebar from '../components/AdminSidebar';

export default function MessagesPage() {
  const { user, isAuthenticated } = useAuthStore();
  const { notifications, loading, fetchNotifications, markAsRead, markAllAsRead } = useNotificationStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);
  const [hasInitialized, setHasInitialized] = useState(false);

  // 비로그인 시 리다이렉트
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // 알림 목록 로드
  useEffect(() => {
    if (isAuthenticated) {
      fetchNotifications();
    }
  }, [isAuthenticated, fetchNotifications]);

  // URL 파라미터에서 메시지 ID 확인 (알림에서 클릭한 경우) - 초기화 시 한 번만 실행
  useEffect(() => {
    if (notifications.length > 0 && !hasInitialized) {
      const messageId = searchParams.get('id');
      if (messageId) {
        setSelectedMessageId(messageId);
        // URL 파라미터로 들어온 메시지가 읽지 않은 상태라면 자동으로 읽음 처리
        const message = notifications.find((n) => n.id === messageId);
        if (message && !message.is_read) {
          markAsRead(messageId).catch((error) => {
            console.error('Failed to mark as read:', error);
          });
        }
      } else if (!selectedMessageId) {
        // 선택된 메시지가 없으면 첫 번째 메시지 선택
        setSelectedMessageId(notifications[0].id);
      }
      setHasInitialized(true);
    }
  }, [notifications.length, hasInitialized, searchParams, selectedMessageId, markAsRead]); // hasInitialized로 한 번만 실행 제어

  if (!isAuthenticated) {
    return null;
  }

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
      // markAllAsRead가 이미 로컬 상태를 업데이트하므로 fetchNotifications 불필요
    } catch (error) {
      console.error('Failed to mark all as read:', error);
      alert('모든 알림을 읽음 처리하는데 실패했습니다.');
    }
  };

  const handleSelectMessage = async (messageId: string) => {
    setSelectedMessageId(messageId);

    // 읽지 않은 메시지를 선택하면 읽음 처리
    const message = notifications.find((n) => n.id === messageId);
    if (message && !message.is_read) {
      try {
        await markAsRead(messageId);
        // markAsRead가 이미 로컬 상태를 업데이트하므로 fetchNotifications 불필요
      } catch (error) {
        console.error('Failed to mark as read:', error);
      }
    }
  };

  const handleActionClick = async (actionUrl: string) => {
    // 인증 우수 관련 페이지로 이동하기 전에 사용자 정보 갱신
    if (actionUrl.includes('/provider/premium')) {
      await useAuthStore.getState().checkSession();
    }
    navigate(actionUrl);
  };

  const selectedMessage = notifications.find((n) => n.id === selectedMessageId);
  const unreadNotifications = notifications.filter((n) => !n.is_read);

  return (
    <div className="h-screen bg-gray-50 flex flex-col overflow-hidden">
      <Header />
      <MainTabsNav className="mt-16" />
      <div className="flex flex-1 max-w-[1440px] mx-auto w-full overflow-hidden">
        {/* 공유 사이드바 */}
        <AdminSidebar />

        {/* 메인 콘텐츠 */}
        <main className="flex-1 flex overflow-hidden">
          {/* 좌측: 메시지 목록 */}
          <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
            {/* 헤더 */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-primary" />
                  알림
                </h2>
                {unreadNotifications.length > 0 && (
                  <button
                    onClick={handleMarkAllAsRead}
                    className="text-xs text-primary hover:text-primary-600 font-medium"
                  >
                    모두 읽음
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-500">
                {unreadNotifications.length > 0
                  ? `읽지 않은 알림 ${unreadNotifications.length}개`
                  : '모든 알림을 확인했습니다'}
              </p>
            </div>

            {/* 메시지 목록 */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                  <p className="text-sm text-gray-500">로딩 중...</p>
                </div>
              ) : notifications.length === 0 ? (
                <div className="p-8 text-center">
                  <Inbox className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">알림이 없습니다</p>
                </div>
              ) : (
                <div>
                  {notifications.map((notification) => (
                    <button
                      key={notification.id}
                      onClick={() => handleSelectMessage(notification.id)}
                      className={`w-full p-4 border-b border-gray-100 text-left hover:bg-gray-50 transition-colors ${
                        selectedMessageId === notification.id ? 'bg-blue-50 border-l-4 border-l-primary' : ''
                      } ${!notification.is_read ? 'bg-blue-50/30' : ''}`}
                    >
                      <div className="flex items-start gap-2">
                        {/* 읽지 않은 표시 */}
                        {!notification.is_read && (
                          <div className="w-2 h-2 bg-blue-500 rounded-full mt-1.5 flex-shrink-0" />
                        )}

                        <div className="flex-1 min-w-0">
                          <h3
                            className={`text-sm font-semibold mb-1 line-clamp-1 ${
                              notification.is_read ? 'text-gray-700' : 'text-gray-900'
                            }`}
                          >
                            {notification.title}
                          </h3>
                          <p className="text-xs text-gray-600 line-clamp-2 mb-1">
                            {notification.message}
                          </p>
                          <p className="text-xs text-gray-400">
                            {new Date(notification.created_at).toLocaleDateString('ko-KR', {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* 우측: 메시지 상세 */}
          <div className="flex-1 bg-gray-50 flex items-center justify-center p-6 overflow-y-auto">
            {!selectedMessage ? (
              <div className="text-center">
                <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">메시지를 선택하세요</h3>
                <p className="text-gray-500">좌측 목록에서 메시지를 선택하면 내용이 표시됩니다.</p>
              </div>
            ) : (
              <div className="max-w-2xl w-full bg-white rounded-xl shadow-sm border border-gray-200 p-8">
                {/* 메시지 헤더 */}
                <div className="mb-6 pb-6 border-b border-gray-200">
                  <div className="flex items-start justify-between mb-2">
                    <h1 className="text-2xl font-bold text-gray-900 flex-1">
                      {selectedMessage.title}
                    </h1>
                    {!selectedMessage.is_read && (
                      <div className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-md text-xs font-medium">
                        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full" />
                        읽지 않음
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-gray-500">
                    {new Date(selectedMessage.created_at).toLocaleString('ko-KR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </p>
                </div>

                {/* 메시지 본문 */}
                <div className="mb-8">
                  <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                    {selectedMessage.message}
                  </p>
                </div>

                {/* 액션 버튼 */}
                {selectedMessage.action_url && selectedMessage.action_label && (
                  <div className="flex justify-center">
                    <button
                      onClick={() => handleActionClick(selectedMessage.action_url!)}
                      className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors font-medium"
                    >
                      {selectedMessage.action_label}
                      <ExternalLink className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* 읽음 상태 표시 */}
                {selectedMessage.is_read && selectedMessage.read_at && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <p className="text-xs text-gray-500 flex items-center gap-2">
                      <CheckCheck className="w-4 h-4" />
                      읽음: {new Date(selectedMessage.read_at).toLocaleString('ko-KR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
      </div>
      <Footer />
    </div>
  );
}
